import React, { useState } from 'react';
import { useAlert } from '../context/AlertContext';
import CountyDropdown from './CountyDropdown';
import FacilityDropdown from './FacilityDropdown';

const DetentionForm = ({ onSubmit, loading, linkedImmigrant = null }) => {
  const { showSuccess, showError } = useAlert();

  // Format date for display - consistent with RecordDetailsModal
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // If the date string is in YYYY-MM-DD format, treat it as a local date
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // For other date formats, use the original logic
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  // Get current month in YYYY-MM format for default
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [formData, setFormData] = useState({
    reportMonth: getCurrentMonth(), // Auto-populate to current month
    facilityName: '',
    facilityType: 'COUNTY_JAIL',
    county: linkedImmigrant?.contact_county || linkedImmigrant?.contactCounty || '',
    caseNumber: '',
    fbiNumber: '',
    
    // County Jail Data
    iceDetainerReceived: false,
    accompaniedByI200: false,
    accompaniedByI205: false,
    detainerLodged: false,
    detainerCancelledByIce: false,
    releasedIceTookCustody: false,
    iceFailedPickup: false,
    releasedDueToTimeout: false,
    highestOffenseClassification: '',
    releasedAfter48hrViolentCrime: false,
    releasedBeforeIceDetainer: false,
    jemDetainerBeforeIceMatch: false,
    transportedToIceOffice: false,
    housingCostReimbursement: false,
    iceReimbursementRate: '',
    bedDaysIncurred: '',
    isJuvenile: false,
    actualHousingCost: '',
    reimbursementDifference: '',
    releasedHousingCostsUpdates: false, // New checkbox for showing reimbursement field

    // Release tracking fields
    bookOutDate: '',
    releasedMonth: '',

    // FDOC Data
    fdocConfirmedAlienWithDetainer: false,
    fdocConfirmedAlienWithoutDetainer: false,
    fdocIceDetainerRemoved: false,
    fdocReleasedToIceCustody: false,
    fdocScheduledRelease30Days: false,
    fdocReleasedIceRefused: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    // Pre-fill book out date when "Released / Housing Costs Updated" is checked
    if (name === 'releasedHousingCostsUpdates' && checked) {
      const today = new Date();
      const bookOutDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      const releasedMonth = today.toISOString().slice(0, 7); // YYYY-MM format

      updatedFormData = {
        ...updatedFormData,
        bookOutDate: bookOutDate,
        releasedMonth: releasedMonth
      };
    }

    // Clear book out date and released month when "Released / Housing Costs Updated" is unchecked
    if (name === 'releasedHousingCostsUpdates' && !checked) {
      updatedFormData = {
        ...updatedFormData,
        bookOutDate: '',
        releasedMonth: ''
      };
    }

    // Auto-populate released month when book out date changes
    if (name === 'bookOutDate' && value) {
      const releasedMonth = value.slice(0, 7); // Extract YYYY-MM from YYYY-MM-DD
      updatedFormData = {
        ...updatedFormData,
        releasedMonth: releasedMonth
      };
    }

    // Clear released month when book out date is cleared
    if (name === 'bookOutDate' && !value) {
      updatedFormData = {
        ...updatedFormData,
        releasedMonth: ''
      };
    }

    // Auto-calculate housing costs when bed days or juvenile status changes
    if ((name === 'bedDaysIncurred' && value) || (name === 'isJuvenile')) {
      const bedDays = parseInt(name === 'bedDaysIncurred' ? value : updatedFormData.bedDaysIncurred) || 0;
      const isJuvenile = name === 'isJuvenile' ? checked : updatedFormData.isJuvenile;
      const dailyRate = isJuvenile ? 23.30 : 80.86; // Juvenile: $23.30, Adult: $80.86
      const iceReimbursement = 50.00; // Flat fee per person

      if (bedDays >= 0) {
        const actualHousingCost = bedDays * dailyRate;

        if (bedDays === 0) {
          // No reimbursement for 0 bed days
          updatedFormData = {
            ...updatedFormData,
            iceReimbursementRate: '0.00',
            actualHousingCost: actualHousingCost.toFixed(2),
            reimbursementDifference: '0.00'
          };
        } else {
          // Normal calculation for 1+ bed days
          // Note: reimbursementDifference calculation now simplified since housingCostReimbursement is boolean
          const reimbursementDifference = actualHousingCost - iceReimbursement;

          updatedFormData = {
            ...updatedFormData,
            iceReimbursementRate: iceReimbursement.toFixed(2),
            actualHousingCost: actualHousingCost.toFixed(2),
            reimbursementDifference: reimbursementDifference.toFixed(2)
          };
        }
      }
    }

    setFormData(updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.reportMonth || !formData.facilityName || !formData.facilityType) {
      showError('Please fill in all required fields (Report Month, Facility Name, and Facility Type)');
      return;
    }

    try {
      // Include immigrant ID if this is a linked record
      const submissionData = {
        ...formData,
        immigrantId: linkedImmigrant?.id || null
      };

      await onSubmit(submissionData);
      showSuccess('Detention record created successfully');
      
      // Reset form (preserve linked immigrant's county if available)
      setFormData({
        reportMonth: getCurrentMonth(), // Auto-populate to current month
        facilityName: '',
        facilityType: 'COUNTY_JAIL',
        county: linkedImmigrant?.contact_county || linkedImmigrant?.contactCounty || '',
        caseNumber: '',
        fbiNumber: '',
        iceDetainerReceived: false,
        accompaniedByI200: false,
        accompaniedByI205: false,
        detainerLodged: false,
        detainerCancelledByIce: false,
        releasedIceTookCustody: false,
        iceFailedPickup: false,
        releasedDueToTimeout: false,
        highestOffenseClassification: '',
        releasedAfter48hrViolentCrime: false,
        releasedBeforeIceDetainer: false,
        jemDetainerBeforeIceMatch: false,
        transportedToIceOffice: false,
        housingCostReimbursement: false,
        iceReimbursementRate: '',
        bedDaysIncurred: '',
        isJuvenile: false,
        actualHousingCost: '',
        reimbursementDifference: '',
        releasedHousingCostsUpdates: false,

        // Release tracking fields
        bookOutDate: '',
        releasedMonth: '',

        fdocConfirmedAlienWithDetainer: false,
        fdocConfirmedAlienWithoutDetainer: false,
        fdocIceDetainerRemoved: false,
        fdocReleasedToIceCustody: false,
        fdocScheduledRelease30Days: false,
        fdocReleasedIceRefused: false
      });
    } catch (error) {
      showError(error.message || 'Failed to create detention record');
    }
  };

  const offenseClassifications = [
    'Capital Felony',
    'Life Felony',
    '1st Degree Felony',
    '2nd Degree Felony',
    '3rd Degree Felony',
    '1st Degree Misdemeanor',
    '2nd Degree Misdemeanor',
    'Noncriminal Violation'
  ];

  return (
    <form onSubmit={handleSubmit} className="detentionForm">
      {/* Linked Immigrant Information */}
      {linkedImmigrant && (
        <div className="formSection" style={{ backgroundColor: '#f0f8ff', border: '2px solid #4CAF50', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ color: '#2e7d32', marginBottom: '15px' }}> Linked to Immigrant Record</h3>
          <div className="linkedImmigrantInfo" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <strong>Name:</strong> {(linkedImmigrant.first_name || linkedImmigrant.firstName)} {(linkedImmigrant.last_name || linkedImmigrant.lastName)}
            </div>
            <div>
              <strong>Record ID:</strong> {linkedImmigrant.id}
            </div>
            <div>
              <strong>Citizenship:</strong> {linkedImmigrant.country_of_citizenship || linkedImmigrant.countryOfCitizenship || 'N/A'}
            </div>
            <div>
              <strong>Contact Date:</strong> {
                linkedImmigrant.contact_date ? formatDate(linkedImmigrant.contact_date) :
                linkedImmigrant.contactDate ? formatDate(linkedImmigrant.contactDate) : 'N/A'
              }
            </div>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="formSection">
        <h3>Basic Information</h3>
        
        <div className="formRow">
          <div className="formGroup">
            <label><strong>Report Month *</strong></label>
            <input
              type="month"
              name="reportMonth"
              value={formData.reportMonth}
              onChange={handleInputChange}
              className="modern-input"
              required
            />
          </div>
          
          <div className="formGroup">
            <label><strong>Facility Type *</strong></label>
            <select
              name="facilityType"
              value={formData.facilityType}
              onChange={handleInputChange}
              className="modern-input"
              required
            >
              <option value="COUNTY_JAIL">County Jail</option>
              <option value="FDOC">Florida Department of Corrections</option>
            </select>
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Facility Name *</strong></label>
            <FacilityDropdown
              name="facilityName"
              value={formData.facilityName}
              onChange={handleInputChange}
              className="modern-input"
              required
            />
          </div>
          
          <div className="formGroup">
            <label><strong>County</strong></label>
            <CountyDropdown
              name="county"
              value={formData.county}
              onChange={handleInputChange}
              className="modern-input"
            />
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Case/Booking Number</strong></label>
            <input
              type="text"
              name="caseNumber"
              value={formData.caseNumber}
              onChange={handleInputChange}
              className="modern-input"
              placeholder="Enter case or booking number"
            />
          </div>

          <div className="formGroup">
            <label><strong>FBI Number</strong></label>
            <input
              type="text"
              name="fbiNumber"
              value={formData.fbiNumber}
              onChange={handleInputChange}
              className="modern-input"
              placeholder="Enter FBI number (optional)"
            />
          </div>
        </div>
      </div>

      {/* County Jail Data Section */}
      {formData.facilityType === 'COUNTY_JAIL' && (
        <div className="formSection">
          <h3>County Jail Data Collection</h3>
          
          {/* Item 1: ICE Detainer Forms */}
          <div className="formSubsection">
            <h4>1. ICE Detainer Forms (I-247)</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="iceDetainerReceived"
                  checked={formData.iceDetainerReceived}
                  onChange={handleInputChange}
                />
                ICE Detainer Received (I-247)
              </label>
              
              {formData.iceDetainerReceived && (
                <div className="subCheckboxes">
                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      name="accompaniedByI200"
                      checked={formData.accompaniedByI200}
                      onChange={handleInputChange}
                    />
                    Accompanied by I-200 (Probable Cause Arrest Warrant)
                  </label>
                  
                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      name="accompaniedByI205"
                      checked={formData.accompaniedByI205}
                      onChange={handleInputChange}
                    />
                    Accompanied by I-205 (Warrant of Removal)
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Item 2: Detainer Lodged and Cancelled */}
          <div className="formSubsection">
            <h4>2. Detainer Status</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="detainerLodged"
                  checked={formData.detainerLodged}
                  onChange={handleInputChange}
                />
                Hold Placed
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="detainerCancelledByIce"
                  checked={formData.detainerCancelledByIce}
                  onChange={handleInputChange}
                />
                Hold Cancelled by ICE
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedIceTookCustody"
                  checked={formData.releasedIceTookCustody}
                  onChange={handleInputChange}
                />
                ICE Took Custody of Inmate
              </label>
            </div>
          </div>

          {/* Item 3: 48-Hour Period */}
          <div className="formSubsection">
            <h4>3. 48-Hour ICE Custody Period</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="iceFailedPickup"
                  checked={formData.iceFailedPickup}
                  onChange={handleInputChange}
                />
                ICE Failed to Take Custody Within 48 Hours
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedDueToTimeout"
                  checked={formData.releasedDueToTimeout}
                  onChange={handleInputChange}
                />
                Released Due to 48-Hour Timeout
              </label>
            </div>
          </div>

          {/* Item 4: Offense Classification */}
          <div className="formSubsection">
            <h4>4. Highest State Law Offense Classification</h4>
            <select
              name="highestOffenseClassification"
              value={formData.highestOffenseClassification}
              onChange={handleInputChange}
              className="modern-input"
            >
              <option value="">Select offense classification</option>
              {offenseClassifications.map(classification => (
                <option key={classification} value={classification}>
                  {classification}
                </option>
              ))}
            </select>
          </div>

          {/* Items 5-8: Additional County Jail Data */}
          <div className="formSubsection">
            <h4>5-8. Additional Data</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedAfter48hrViolentCrime"
                  checked={formData.releasedAfter48hrViolentCrime}
                  onChange={handleInputChange}
                />
                Released After 48hr + Charged with Violent Crime
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedBeforeIceDetainer"
                  checked={formData.releasedBeforeIceDetainer}
                  onChange={handleInputChange}
                />
                Released Before ICE Detainer Received
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="jemDetainerBeforeIceMatch"
                  checked={formData.jemDetainerBeforeIceMatch}
                  onChange={handleInputChange}
                />
                Jail Enforcement Model Detainer Before ICE Match
              </label>
              
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="transportedToIceOffice"
                  checked={formData.transportedToIceOffice}
                  onChange={handleInputChange}
                />
                Transported to ICE Office by Detention
              </label>
            </div>
          </div>

          {/* Item 9: Housing Cost Data */}
          <div className="formSubsection">
            <h4>9. Housing Cost Reimbursement Data</h4>
            <div className="formRow">
              <div className="formGroup">
                <label>Bed Days Incurred *</label>
                <input
                  type="number"
                  name="bedDaysIncurred"
                  value={formData.bedDaysIncurred}
                  onChange={handleInputChange}
                  className="modern-input"
                  placeholder="Enter number of bed days"
                  min="0"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Enter bed days to auto-calculate costs
                </small>
              </div>

              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    name="isJuvenile"
                    checked={formData.isJuvenile}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  Juvenile (Under 18)
                </label>
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    name="releasedHousingCostsUpdates"
                    checked={formData.releasedHousingCostsUpdates}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  Released / Housing Costs Updated
                </label>
              </div>
            </div>

            {formData.releasedHousingCostsUpdates && (
              <>
                <div className="formRow">
                  <div className="formGroup">
                    <label>
                      <input
                        type="checkbox"
                        name="housingCostReimbursement"
                        checked={formData.housingCostReimbursement}
                        onChange={handleInputChange}
                        style={{ marginRight: '8px' }}
                      />
                      Housing Cost Reimbursement Received
                    </label>
                  </div>
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label><strong>Book Out Date</strong></label>
                    <input
                      type="date"
                      name="bookOutDate"
                      value={formData.bookOutDate}
                      onChange={handleInputChange}
                      className="modern-input"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Date when inmate was released
                    </small>
                  </div>

                  <div className="formGroup">
                    <label><strong>Released Month</strong></label>
                    <input
                      type="month"
                      name="releasedMonth"
                      value={formData.releasedMonth}
                      readOnly
                      className="modern-input"
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Auto-calculated from Book Out Date
                    </small>
                  </div>
                </div>
              </>
            )}

            <div className="formRow">
              <div className="formGroup">
                <label>ICE Reimbursement Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="iceReimbursementRate"
                  value={formData.iceReimbursementRate}
                  onChange={handleInputChange}
                  className="modern-input"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Auto-calculated: $50.00 flat fee (if bed days > 0), but can be manually adjusted
                </small>
              </div>

              <div className="formGroup">
                <label>Actual Housing Cost ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="actualHousingCost"
                  value={formData.actualHousingCost}
                  onChange={handleInputChange}
                  className="modern-input"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Auto-calculated: Bed days × {formData.isJuvenile ? '$23.30' : '$80.86'}, but can be manually adjusted
                </small>
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label>Reimbursement Difference ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="reimbursementDifference"
                  value={formData.reimbursementDifference}
                  onChange={handleInputChange}
                  className="modern-input"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Auto-calculated: Actual cost - ICE reimbursement, but can be manually adjusted
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FDOC Data Section */}
      {formData.facilityType === 'FDOC' && (
        <div className="formSection">
          <h3>Florida Department of Corrections Data</h3>
          
          <div className="checkboxGroup">
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocConfirmedAlienWithDetainer"
                checked={formData.fdocConfirmedAlienWithDetainer}
                onChange={handleInputChange}
              />
              1. Confirmed Criminal Alien with ICE Detainer
            </label>
            
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocConfirmedAlienWithoutDetainer"
                checked={formData.fdocConfirmedAlienWithoutDetainer}
                onChange={handleInputChange}
              />
              2. Confirmed Criminal Alien without ICE Detainer
            </label>
            
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocIceDetainerRemoved"
                checked={formData.fdocIceDetainerRemoved}
                onChange={handleInputChange}
              />
              3. ICE Detainer Removed by ICE
            </label>
            
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocReleasedToIceCustody"
                checked={formData.fdocReleasedToIceCustody}
                onChange={handleInputChange}
              />
              4. Released to ICE Custody
            </label>
            
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocScheduledRelease30Days"
                checked={formData.fdocScheduledRelease30Days}
                onChange={handleInputChange}
              />
              5. Scheduled Release Within 30 Days
            </label>
            
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocReleasedIceRefused"
                checked={formData.fdocReleasedIceRefused}
                onChange={handleInputChange}
              />
              6. Released to Community (ICE Refused Custody)
            </label>
          </div>
        </div>
      )}

      <div className="formActions">
        <button 
          type="submit" 
          className="modern-btn"
          disabled={loading}
        >
          {loading ? 'Adding Record...' : 'Add Detention Record'}
        </button>
      </div>
    </form>
  );
};

export default DetentionForm;
